import { useReducer } from 'react';
import _ from 'lodash';
import { useMutation } from '@apollo/client';
import {
  CreateFarmInRowPatternMutation,
  DeleteFarmInRowPatternMutation,
  UpdateFarmInRowPatternMutation,
} from '../mutations';

const initialPatternsState = {};

const inRowPatternsReducer = (inRowPatterns, action) => {
  switch (action.type) {
    case 'replaceAll': {
      return _.keyBy(action.inRowPatterns, 'id');
    }
    case 'add': {
      return {
        ...inRowPatterns,
        [action.inRowPattern.id]: action.inRowPattern,
      };
    }
    case 'remove': {
      const newPatterns = { ...inRowPatterns };
      delete newPatterns[action.id];
      return newPatterns;
    }
    case 'update': {
      return {
        ...inRowPatterns,
        [action.inRowPattern.id]: action.inRowPattern,
      };
    }
    case 'reset': {
      return initialPatternsState;
    }
    default:
      throw new Error(`Invalid type: ${action.type}.`);
  }
};

const usePatterns = () => {
  const [inRowPatterns, dispatch] = useReducer(
    inRowPatternsReducer,
    initialPatternsState,
  );

  const [createRemoteInRowPattern] = useMutation(
    CreateFarmInRowPatternMutation,
  );
  const [updateRemoteInRowPattern] = useMutation(
    UpdateFarmInRowPatternMutation,
  );
  const [deleteRemoteInRowPattern] = useMutation(
    DeleteFarmInRowPatternMutation,
  );

  const updateInRowPattern = ({ id, ...update }) => {
    const oldInRowPattern = inRowPatterns[id];
    if (!oldInRowPattern) {
      return Promise.reject(
        new Error("Can't update a pattern that no longer exists."),
      );
    }
    return updateRemoteInRowPattern({
      variables: {
        input: { id, updatedAt: oldInRowPattern.updatedAt, ...update },
      },
    }).then((response) => {
      const { result } = response.data?.updateFarmInRowPattern || {};
      if (result) {
        dispatch({
          type: 'update',
          inRowPattern: { ...oldInRowPattern, ...result },
        });
      }
      return Promise.resolve(response);
    });
  };

  const removeInRowPattern = (id) => {
    const updatedAt = inRowPatterns[id]?.updatedAt;
    return deleteRemoteInRowPattern({
      variables: { input: { id, updatedAt } },
    }).then((response) => {
      const { result } = response.data?.deleteFarmInRowPattern || {};
      if (result) {
        dispatch({ type: 'remove', id });
      }
      return Promise.resolve(response);
    });
  };

  const createInRowPattern = (farmId, inRowPattern) => {
    return createRemoteInRowPattern({
      variables: {
        input: {
          farmId,
          ...inRowPattern,
          // Update crop to cropId for mutation
          cropSequence: inRowPattern.cropSequence.map((item) => ({
            ...item,
            crop: undefined,
            cropId: item.crop.id,
            cropVariety: undefined,
            cropVarietyId: item.cropVariety?.id,
          })),
        },
      },
    }).then((response) => {
      const { result } = response.data?.createFarmInRowPattern || {};
      if (result) {
        dispatch({ type: 'add', inRowPattern: { ...inRowPattern, ...result } });
      }
      return Promise.resolve(response);
    });
  };

  const replaceInRowPatterns = (newInRowPatterns) => {
    dispatch({ type: 'replaceAll', inRowPatterns: newInRowPatterns });
  };

  const resetInRowPatterns = () => dispatch({ type: 'reset' });

  return {
    inRowPatterns: Object.values(inRowPatterns),
    createInRowPattern, // Create a new in row pattern.
    replaceInRowPatterns, // Replace all in row patterns with the new list.
    removeInRowPattern, // Remove an in row pattern by id.
    updateInRowPattern, // Update an in row pattern by id.
    getInRowPattern: (id) => inRowPatterns[id], // Get an in row pattern by id.
    resetInRowPatterns, // Reset state to initial state
    inRowPatternsMap: inRowPatterns,
  };
};

export default usePatterns;
